/**
 * 国家、州/省、城市数据工具
 * 使用 react-country-state-city 替代 react-select-country-list
 */
import { GetCountries, GetState, GetCity, GetPhonecodes } from 'react-country-state-city'

// 国家选项类型
export interface CountryOption {
  value: string  // ISO2 代码
  label: string  // 国家名称
  id: number     // 国家ID
}

// 州/省选项类型
export interface StateOption {
  value: string  // 州/省名称
  label: string  // 州/省名称
  id: number     // 州/省ID
}

// 城市选项类型
export interface CityOption {
  value: string  // 城市名称
  label: string  // 城市名称
  id: number     // 城市ID
}

// 国家区号类型
export interface CountryCode {
  code: string      // ISO2 代码
  name: string      // 国家名称
  dialCode: string  // 电话区号
}

/**
 * 获取所有国家列表
 */
export const getCountryList = async (): Promise<CountryOption[]> => {
  try {
    const countries = await GetCountries()
    return countries.map((country: any) => ({
      value: country.iso2,
      label: country.name,
      id: country.id
    }))
  } catch (error) {
    console.error('获取国家列表失败:', error)
    return []
  }
}

/**
 * 根据国家ID获取州/省列表
 */
export const getStateList = async (countryId: number): Promise<StateOption[]> => {
  try {
    const states = await GetState(countryId)
    return states.map((state: any) => ({
      value: state.name,
      label: state.name,
      id: state.id
    }))
  } catch (error) {
    console.error('获取州/省列表失败:', error)
    return []
  }
}

/**
 * 根据国家ID和州/省ID获取城市列表
 */
export const getCityList = async (countryId: number, stateId: number): Promise<CityOption[]> => {
  try {
    const cities = await GetCity(countryId, stateId)
    return cities.map((city: any) => ({
      value: city.name,
      label: city.name,
      id: city.id
    }))
  } catch (error) {
    console.error('获取城市列表失败:', error)
    return []
  }
}

/**
 * 获取所有国家电话区号列表
 */
export const getCountryPhoneCodes = async (): Promise<CountryCode[]> => {
  try {
    const phonecodes = await GetPhonecodes()
    console.log('原始电话区号数据:', phonecodes) // 调试日志
    
    if (!phonecodes || phonecodes.length === 0) {
      console.warn('电话区号数据为空')
      return []
    }
    
    return phonecodes.map((item: any) => ({
      code: item.iso2 || item.code || '',
      name: item.name || '',
      dialCode: item.phone_code ? `+${item.phone_code}` : (item.dialCode || '')
    }))
  } catch (error) {
    console.error('获取国家区号列表失败:', error)
    return []
  }
}

/**
 * 根据国家ISO2代码查找国家ID
 */
export const getCountryIdByIso2 = async (iso2: string): Promise<number | null> => {
  try {
    const countries = await GetCountries()
    const country = countries.find((c: any) => c.iso2 === iso2)
    return country ? country.id : null
  } catch (error) {
    console.error('查找国家ID失败:', error)
    return null
  }
}

/**
 * 根据国家ID和州/省名称查找州/省ID
 */
export const getStateIdByName = async (countryId: number, stateName: string): Promise<number | null> => {
  try {
    const states = await GetState(countryId)
    const state = states.find((s: any) => s.name === stateName)
    return state ? state.id : null
  } catch (error) {
    console.error('查找州/省ID失败:', error)
    return null
  }
}
